import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Check, Loader2, CreditCard, ArrowLeft, Zap, ShieldCheck } from 'lucide-react'
import { useAuthStore } from '@/stores/auth.store'
import { toast } from 'sonner'

interface PricingPlan {
  id: string
  name: string
  price: number
  originalPrice?: number
  duration: string
  durationDays: number
  discount?: string
  features: string[]
  popular?: boolean
  isActivation?: boolean
}

const activationPlan: PricingPlan = {
  id: 'activation',
  name: 'Attivazione Piattaforma',
  price: 2797,
  duration: 'Una tantum + 6 mesi inclusi',
  durationDays: 182,
  isActivation: true,
  features: [
    'Primo accesso alla piattaforma',
    '6 mesi di abbonamento inclusi',
    'Setup completo del tuo account',
    'Accesso completo al trading',
    'Hedging automatico',
    'Dashboard in tempo reale'
  ]
}

const renewalPlans: PricingPlan[] = [
  {
    id: 'monthly',
    name: 'Mensile',
    price: 399,
    duration: '1 mese',
    durationDays: 30,
    features: [
      'Accesso completo al trading',
      'Hedging automatico',
      'Supporto provider FundedNext',
      'Dashboard in tempo reale',
      'Supporto email'
    ]
  },
  {
    id: 'semestral',
    name: 'Semestrale',
    price: 2121,
    originalPrice: 2394,
    duration: '6 mesi',
    durationDays: 180,
    discount: '15% di sconto',
    popular: true,
    features: [
      'Tutto del piano Mensile',
      'Risparmia 273\u20AC',
      'Priorita\' nel supporto',
      'Accesso anticipato nuove funzionalita\'',
      'Report mensili dettagliati'
    ]
  },
  {
    id: 'annual',
    name: 'Annuale',
    price: 3591,
    originalPrice: 4788,
    duration: '12 mesi',
    durationDays: 365,
    discount: '25% di sconto',
    features: [
      'Tutto del piano Semestrale',
      'Risparmia 1197\u20AC',
      'Supporto prioritario dedicato',
      'Sessione di onboarding 1-to-1',
      'Accesso beta esclusivo'
    ]
  }
]

export const Pricing = () => {
  const navigate = useNavigate()
  const { token, user } = useAuthStore()
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null)

  const hasPaidActivation = user?.has_paid_initial_fee === true
  const hasHadSubscription = hasPaidActivation || user?.subscription_status === 'active' || user?.subscription_status === 'expired'
  const canRenew = hasHadSubscription

  const handlePurchase = async (plan: PricingPlan) => {
    if (!token) {
      navigate('/login')
      return
    }

    setLoadingPlan(plan.id)
    try {
      const response = await fetch('/api/v1/payments/subscription/checkout', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          plan_id: plan.id,
          duration_days: plan.durationDays,
          amount: plan.price * 100
        })
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.detail || 'Errore durante la creazione del pagamento')
      }

      const data = await response.json()
      
      if (data.checkout_url) {
        window.location.href = data.checkout_url
      } else {
        throw new Error('URL di checkout non disponibile')
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Errore durante il pagamento')
    } finally {
      setLoadingPlan(null)
    }
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <div className="mb-6">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Indietro
        </Button>
      </div>

      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold mb-4">Scegli il tuo piano</h1>
        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
          Sblocca tutte le funzionalita' di trading automatico e inizia a proteggere i tuoi account prop
        </p>
      </div>

      {user?.subscription_status === 'expired' && (
        <div className="mb-8 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg text-center">
          <p className="text-amber-600 dark:text-amber-400">
            Il tuo abbonamento e' scaduto. Rinnova ora per continuare ad usare la piattaforma.
          </p>
        </div>
      )}

      {!canRenew && (
        <div className="mb-12">
          <h2 className="text-2xl font-semibold text-center mb-6">Primo Acquisto</h2>
          <div className="max-w-md mx-auto">
            <Card className="relative border-2 border-blue-500/50 shadow-xl shadow-blue-500/10">
              <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600">
                Richiesto per iniziare
              </Badge>
              
              <CardHeader className="text-center pb-4">
                <CardTitle className="text-2xl">{activationPlan.name}</CardTitle>
                <CardDescription>{activationPlan.duration}</CardDescription>
                
                <div className="mt-4">
                  <span className="text-4xl font-bold">{'\u20AC'}{activationPlan.price.toLocaleString()}</span>
                </div>
              </CardHeader>
              
              <CardContent className="space-y-4">
                <ul className="space-y-3">
                  {activationPlan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <Button 
                  className="w-full mt-6 bg-blue-600 hover:bg-blue-700" 
                  size="lg"
                  onClick={() => handlePurchase(activationPlan)}
                  disabled={loadingPlan !== null}
                >
                  {loadingPlan === activationPlan.id ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Elaborazione...
                    </>
                  ) : (
                    <>
                      <Zap className="w-4 h-4 mr-2" />
                      Attiva ora
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {canRenew && (
        <div className="mb-8 p-4 bg-green-500/10 border border-green-500/30 rounded-lg text-center flex items-center justify-center gap-2">
          <ShieldCheck className="w-5 h-5 text-green-500" />
          <p className="text-green-600 dark:text-green-400">
            Piattaforma attivata! Scegli un piano di rinnovo per continuare.
          </p>
        </div>
      )}

      <div className="relative mb-8">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-muted-foreground/20"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="bg-background px-4 text-muted-foreground">
            {canRenew ? 'Piani di rinnovo' : 'Rinnovi successivi (dopo attivazione)'}
          </span>
        </div>
      </div>

      <div className={`grid md:grid-cols-3 gap-6 ${!canRenew ? 'opacity-50 pointer-events-none' : ''}`}>
        {renewalPlans.map((plan) => (
          <Card 
            key={plan.id} 
            className={`relative ${plan.popular ? 'border-primary shadow-lg scale-105' : ''}`}
          >
            {plan.popular && (
              <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary">
                Piu' popolare
              </Badge>
            )}
            
            <CardHeader className="text-center pb-4">
              <CardTitle className="text-2xl">{plan.name}</CardTitle>
              <CardDescription>{plan.duration}</CardDescription>
              
              <div className="mt-4">
                {plan.originalPrice && (
                  <span className="text-lg text-muted-foreground line-through mr-2">
                    {'\u20AC'}{plan.originalPrice.toLocaleString()}
                  </span>
                )}
                <span className="text-4xl font-bold">{'\u20AC'}{plan.price.toLocaleString()}</span>
              </div>
              
              {plan.discount && (
                <Badge variant="secondary" className="mt-2 bg-green-500/20 text-green-600">
                  {plan.discount}
                </Badge>
              )}
            </CardHeader>
            
            <CardContent className="space-y-4">
              <ul className="space-y-3">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
              
              <Button 
                className="w-full mt-6" 
                size="lg"
                variant={plan.popular ? 'default' : 'outline'}
                onClick={() => handlePurchase(plan)}
                disabled={loadingPlan !== null || !canRenew}
              >
                {loadingPlan === plan.id ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Elaborazione...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-4 h-4 mr-2" />
                    Rinnova ora
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="mt-12 text-center text-sm text-muted-foreground">
        <p>Pagamento sicuro tramite Stripe. Puoi disdire in qualsiasi momento.</p>
        <p className="mt-2">Hai domande? Contatta il supporto.</p>
      </div>
    </div>
  )
}
